/**
 * Mulch & Stuff Landscape Material Project Planner
 * Phase 2 — Manual measurement geometry foundation
 *
 * Pure geometry only.
 *
 * Customer-entered dimensions are normalized here and converted into
 * geometry-independent measurement facts:
 *
 *   squareFeet
 *   perimeterFacts
 *
 * Cubic-yard calculations do NOT belong in this module.
 */
(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.MSProjectPlannerGeometry = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const STATUS = Object.freeze({
    INCOMPLETE: 'incomplete',
    INVALID: 'invalid',
    VALID: 'valid'
  });

  const METHODS = Object.freeze({
    RECTANGLE: 'rectangle',
    ROUND_AREA: 'round-area',
    ROUND_BORDER: 'round-border',
    TRIANGLE: 'triangle',
    KNOWN_SQUARE_FOOTAGE: 'known-square-footage'
  });

  function hasValue(value) {
    return value !== null &&
      value !== undefined &&
      String(value).trim() !== '';
  }

  function toFiniteNumber(value) {
    if (!hasValue(value)) {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : NaN;
  }

  function normalizeLinearDimension(input) {
    const source = input || {};
    const feetPresent = hasValue(source.feet);
    const inchesPresent = hasValue(source.inches);

    if (!feetPresent && !inchesPresent) {
      return Object.freeze({
        status: STATUS.INCOMPLETE,
        feet: null
      });
    }

    const feet = feetPresent ? toFiniteNumber(source.feet) : 0;
    const inches = inchesPresent ? toFiniteNumber(source.inches) : 0;

    if (
      !Number.isFinite(feet) ||
      !Number.isFinite(inches) ||
      feet < 0 ||
      inches < 0
    ) {
      return Object.freeze({
        status: STATUS.INVALID,
        feet: null
      });
    }

    const normalizedFeet = feet + (inches / 12);

    if (
      !Number.isFinite(normalizedFeet) ||
      normalizedFeet <= 0
    ) {
      return Object.freeze({
        status: STATUS.INVALID,
        feet: null
      });
    }

    return Object.freeze({
      status: STATUS.VALID,
      feet: normalizedFeet
    });
  }

  function incompleteMeasurement() {
    return Object.freeze({
      status: STATUS.INCOMPLETE,
      squareFeet: null,
      perimeterFacts: Object.freeze({})
    });
  }

  function invalidMeasurement() {
    return Object.freeze({
      status: STATUS.INVALID,
      squareFeet: null,
      perimeterFacts: Object.freeze({})
    });
  }

  function validMeasurement(squareFeet, perimeterFacts) {
    if (!Number.isFinite(squareFeet) || squareFeet <= 0) {
      return invalidMeasurement();
    }

    return Object.freeze({
      status: STATUS.VALID,
      squareFeet,
      perimeterFacts: Object.freeze({
        ...(perimeterFacts || {})
      })
    });
  }

  function combineRequiredDimensions(dimensions) {
    if (
      dimensions.some(function (dimension) {
        return dimension.status === STATUS.INVALID;
      })
    ) {
      return STATUS.INVALID;
    }

    if (
      dimensions.some(function (dimension) {
        return dimension.status === STATUS.INCOMPLETE;
      })
    ) {
      return STATUS.INCOMPLETE;
    }

    return STATUS.VALID;
  }

  function deriveRectangle(inputs) {
    const source = inputs || {};
    const length = normalizeLinearDimension(source.length);
    const width = normalizeLinearDimension(source.width);
    const status = combineRequiredDimensions([length, width]);

    if (status === STATUS.INVALID) {
      return invalidMeasurement();
    }

    if (status === STATUS.INCOMPLETE) {
      return incompleteMeasurement();
    }

    const squareFeet = length.feet * width.feet;
    const perimeterFeet = 2 * (length.feet + width.feet);

    return validMeasurement(squareFeet, {
      totalFeet: perimeterFeet
    });
  }

  function deriveRoundArea(inputs) {
    const source = inputs || {};
    const diameter = normalizeLinearDimension(source.diameter);

    if (diameter.status === STATUS.INVALID) {
      return invalidMeasurement();
    }

    if (diameter.status === STATUS.INCOMPLETE) {
      return incompleteMeasurement();
    }

    const radius = diameter.feet / 2;
    const squareFeet = Math.PI * radius * radius;
    const circumference = Math.PI * diameter.feet;

    return validMeasurement(squareFeet, {
      totalFeet: circumference
    });
  }

  function deriveRoundBorder(inputs) {
    const source = inputs || {};
    const insideDiameter = normalizeLinearDimension(
      source.insideDiameter
    );
    const borderWidth = normalizeLinearDimension(
      source.borderWidth
    );

    const status = combineRequiredDimensions([
      insideDiameter,
      borderWidth
    ]);

    if (status === STATUS.INVALID) {
      return invalidMeasurement();
    }

    if (status === STATUS.INCOMPLETE) {
      return incompleteMeasurement();
    }

    const outsideDiameter =
      insideDiameter.feet + (2 * borderWidth.feet);

    const insideRadius = insideDiameter.feet / 2;
    const outsideRadius = outsideDiameter / 2;

    const squareFeet =
      Math.PI *
      (
        (outsideRadius * outsideRadius) -
        (insideRadius * insideRadius)
      );

    return validMeasurement(squareFeet, {
      insideFeet: Math.PI * insideDiameter.feet,
      outsideFeet: Math.PI * outsideDiameter
    });
  }

  function deriveTriangle(inputs) {
    const source = inputs || {};
    const sides = [source.side1, source.side2, source.side3].map(normalizeLinearDimension);
    const status = combineRequiredDimensions(sides);

    if (status === STATUS.INVALID) {
      return invalidMeasurement();
    }

    if (status === STATUS.INCOMPLETE) {
      return incompleteMeasurement();
    }

    // Sorted, scaled Heron form avoids cancellation near a flat triangle
    // and avoids overflowing intermediate products for large dimensions.
    const [a, b, c] = sides.map(side => side.feet).sort((x, y) => y - x);
    const gap = c - (a - b);
    if (gap <= Number.EPSILON * a * 8) return invalidMeasurement();
    const areaFactor = Math.sqrt(
      (1 + b / a + c / a) * (gap / a) *
      ((c + (a - b)) / a) * (1 + (b - c) / a)
    ) / 4;
    return validMeasurement((areaFactor * a) * a, {});
  }

  function deriveKnownSquareFootage(inputs) {
    const source = inputs || {};
    const squareFeet = toFiniteNumber(source.squareFeet);

    if (squareFeet === null) {
      return incompleteMeasurement();
    }

    if (!Number.isFinite(squareFeet) || squareFeet <= 0) {
      return invalidMeasurement();
    }

    return validMeasurement(squareFeet, {});
  }

  function deriveMeasurement(method, inputs) {
    switch (method) {
      case METHODS.RECTANGLE:
        return deriveRectangle(inputs);

      case METHODS.ROUND_AREA:
        return deriveRoundArea(inputs);

      case METHODS.ROUND_BORDER:
        return deriveRoundBorder(inputs);

      case METHODS.TRIANGLE:
        return deriveTriangle(inputs);

      case METHODS.KNOWN_SQUARE_FOOTAGE:
        return deriveKnownSquareFootage(inputs);

      case null:
      case undefined:
      case '':
        return incompleteMeasurement();

      default:
        return invalidMeasurement();
    }
  }

  return Object.freeze({
    STATUS,
    METHODS,
    normalizeLinearDimension,
    deriveRectangle,
    deriveRoundArea,
    deriveRoundBorder,
    deriveTriangle,
    deriveKnownSquareFootage,
    deriveMeasurement
  });
});
